import { useState, useEffect, useMemo } from "react";
import { Button, Modal, message } from "antd";

import TaskForm from "../../../task/TaskForm";

import { updateTask } from "../../../../api/task";
import { cn, getQueryParamsString } from "../../../../lib/utils";
import dayjs from "dayjs";
import { startOfDay } from "date-fns";
import { useNavigate } from "react-router-dom";

const DayEquipmentTaskTile = ({
  task,
  className,
  onUpdate,
  date,
  currentHour,
  continuesFromPreviousDay,
}) => {
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [taskFormInstance, setTaskFormInstance] = useState();
  const [worklogs, setWorklogs] = useState([]);
  const navigate = useNavigate()

  const cellWidth = 130;

  // This is the hour of the cell in which this component is being rendered
  const currentHourDayjs = dayjs(currentHour);
  // This is the EOD of the current date being rendered
  const endOfDay = currentHourDayjs.endOf("day");

  const taskStartHourDayjs = dayjs(task.startDate);
  const taskEndHourDayjs = dayjs(task.dueDate);

  // Cap the taskEndHourDayjs till midnight so that it doesn't expand beyond the horizontal scrollable area
  const effectiveEndHourDayjs = taskEndHourDayjs.isAfter(endOfDay)
    ? endOfDay
    : taskEndHourDayjs;

  // Handle tasks that started yesterday (or earlier) but continue into today
  const effectiveStartHourDayjs = continuesFromPreviousDay
    ? startOfDay(currentHour) // Use start of current day instead of actual task start
    : taskStartHourDayjs;

  // Calculate horizontal offset from cell start (in minutes):
  // Used to calculate left margin of tile
  const offsetInMinutes = useMemo(
    () => currentHourDayjs.diff(effectiveStartHourDayjs, "minute"),
    [effectiveStartHourDayjs, currentHourDayjs]
  );

  // Used to calculate tile width
  const taskLengthInMinutes = useMemo(
    () => effectiveEndHourDayjs.diff(effectiveStartHourDayjs, "minute"),
    [effectiveStartHourDayjs, effectiveEndHourDayjs]
  );

  const leftMargin = useMemo(
    () => (Math.abs(offsetInMinutes) / 60) * cellWidth,
    [offsetInMinutes]
  );

  const taskWidth = useMemo(
    () => (Math.abs(taskLengthInMinutes) / 60) * cellWidth,
    [taskLengthInMinutes]
  );

  // Action functions for footer buttons
  const handleViewWorklogs = () => {
    const projectId = task.project.id
    const taskId = task.id
    const queryParams = getQueryParamsString({ taskId, view: "True" })

    navigate(`/worklogs?${queryParams}`)
  }

  const handleCreateWorklog = () => {
    const projectId = task.project.id
    const taskId = task.id
    const queryParams = getQueryParamsString({ projectId, taskId, create: "True" })

    navigate(`/worklogs?${queryParams}`)
  }

  const handleOk = async () => {
    const values = await taskFormInstance?.validateFields();
    const { workLogs, project, ...taskData } = task;

    let projectId = typeof (values.project) == "number" ? values.project : values.project.value;

    try {
      await updateTask({
        ...taskData,
        ...values,
        project: projectId,
      }).then(async () => {
        message.success("Task updated successfully");
        await onUpdate();
        setShowTaskForm(false);
      });
    } catch (error) {
      message.error("Failed to update task");
      console.error("Failed:", error);
    }
  }

  const handleCancel = () => setShowTaskForm(false)

  useEffect(() => {
    let activeWorklogs = (task.workLogs || []).filter((w) => w.isActive);
    if (!activeWorklogs.length) return;

    // Filtering out the worklogs that overlap the task
    const workLogsForTheHour = activeWorklogs.filter((w) => {
      const taskStart = dayjs(task.startDate);
      const taskDue = dayjs(task.dueDate);
      const workStart = dayjs(w.startAt);
      const workEnd = dayjs(w.endAt);

      return (
        currentHourDayjs.isSame(workStart, "day") && // Worklog is of the same day as current date
        // Checking for overlap between worklog and task
        workStart.isBefore(taskDue) &&
        workEnd.isAfter(taskStart)
      );
    });

    setWorklogs(workLogsForTheHour);
  }, [task, date]);

  if (!task || !task.project || !task.project.client) return null;

  return (
    <div
      className="absolute z-10 h-full p-1.5"
      style={{
        width: `${taskWidth}px`,
        marginLeft: `${leftMargin}px`,
        important: "important",
      }}
    >
      <div
        className={cn(
          "rounded-lg  cursor-pointer group hover:bg-gray-500 bg-sky-200 text-sky-700 h-full",
          worklogs.length && "bg-orange-200 text-orange-800",
          worklogs.some((w) => w.isDownloaded) && "bg-green-200 text-green-900",
          className
        )}
        onClick={() => setShowTaskForm(true)}
      >
        <div className="w-1 group-hover:bg-orange-500"></div>
        <div className="p-2  flex flex-col justify-around">
          <p className="text-xs group-hover:text-white">
            {task.project.client.name}
          </p>
          <p className="text-xs font-semibold group-hover:text-white truncate overflow-ellipsis">
            {task.project.name}
          </p>
          <p className="font-light text-xs group-hover:text-white">
            {task.assignee.firstName + " " + task.assignee.lastName || ""}
          </p>
        </div>
      </div>
      <Modal
        title="Update Task"
        open={showTaskForm}
        // Keeping the onCancel and onOk so that the modal works with other cancel and ok events (Eg: pressing `Esc`)
        onCancel={handleCancel}
        onOk={handleOk}
        footer={<div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div>
            {/* Always show the create worklog button */}
            <Button key="create-worklog" style={{ marginRight: 8 }} onClick={handleCreateWorklog}>
              Create Worklog
            </Button>

            {/* Show the download worklogs button only if there is any */}
            {task.workLogs.length > 0 &&
              <Button key="download" onClick={handleViewWorklogs}>
                View Worklogs ({task.workLogs.length})
              </Button>
            }

          </div>
          <div>
            <Button key="cancel" onClick={handleCancel} style={{ marginRight: 8 }}>
              Cancel
            </Button>
            <Button
              key="submit"
              type="primary"
              onClick={handleOk}
            >
              OK
            </Button>
          </div>
        </div>}
      >
        <TaskForm
          onFormInstanceReady={(instance) => {
            setTaskFormInstance(instance);
          }}
          initialValues={{
            ...task,
            project: { value: task.project.id, label: task.project.name },
          }}
        />
      </Modal>
    </div>
  );
};

export default DayEquipmentTaskTile;
